/* eslint-disable consistent-return */
import React, { createContext, useState } from 'react';
import { getTMXSessionId, gotoLogonModule } from '../../components/Utils';
import {
  B2B_VALIDATE_AGREEMENT_CODE,
  B2B_ENROLL_AGREEMENT_CODE,
  B2B_UNENROLL_AGREEMENT_CODE,
} from '../../constants/links';
import {
  newrelic,
  PRICING_BOOK_NATIONAL_ENROLL,
  PRICING_BOOK_NATIONAL_UNENROLL,
} from '../../utils/newrelic';

const NationalAccountContext = createContext<any>(null);

interface IProviderProps {
  children: React.ReactNode;
}

const errorMessageHelper = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  return 'System error';
};

const NationalAccountProvider = ({ children }: IProviderProps) => {
  const [showModal, setShowModal] = useState(false);
  const [errorState, setErrorState] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [currentAgreementCode, setCurrentAgreementCode] = useState({});
  const [validResponseState, setValidResponseState] = useState({
    enrollState: false,
    requiredEINState: {},
    agreementCodeNameEntered: {},
    agreementCodeEntered: {},
    validatedState: {},
    agreementType: {},
    einGovState: false,
  });

  const getUserInfo = React.useCallback(() => {
    const getTHDCustomer = () =>
      typeof window !== 'undefined'
        ? (window as any)?.THDCustomer?.default
        : {};
    const { svocID, userID, jwt } = getTHDCustomer();
    return { svocID, userID, jwt };
  }, []);

  const fetchValidateAgreementCode = React.useCallback(
    async (agreementCode: string) => {
      const url = B2B_VALIDATE_AGREEMENT_CODE;
      const fullURL = `${url}/${agreementCode}`;
      const { jwt } = getUserInfo();

      try {
        const response = await fetch(fullURL, {
          method: 'GET',
          credentials: 'include',
          headers: {
            channelId: '1',
            'Content-Type': 'application/json',
            TMXProfileID: getTMXSessionId(),
            Authorization: jwt,
          },
        });
        if (response.status === 401 || response.status === 431) {
          gotoLogonModule();
        }
        const res = await response.json();
        if (response.status === 404) {
          return {
            data: {},
            status: 404,
            error: 'Agreement Code Could Not Be Found',
            hasError: true,
          };
        }
        if (
          (response.status >= 500 && response.status < 599) ||
          response.status === 403
        ) {
          return {
            data: {},
            status: 500,
            error: '500 Server Error',
            hasError: true,
          };
        }
        if (res?.errors?.length > 0)
          return {
            data: {},
            error: res?.errors[0],
            hasError: true,
          };
        if (response.status === 200 && !res?.errors) {
          return { data: res, error: null, hasError: false };
        }
      } catch (error) {
        const errorMessage = errorMessageHelper(error);

        return {
          data: {},
          error: errorMessage,
          hasError: true,
        };
      }
    },
    [],
  );

  const enrollAgreementCode = React.useCallback(
    async (
      isEnroll: boolean,
      agreementCode: string,
      einCodeUserInput: string,
    ) => {
      const url = isEnroll
        ? B2B_ENROLL_AGREEMENT_CODE
        : B2B_UNENROLL_AGREEMENT_CODE;
      const { jwt } = getUserInfo();

      try {
        const response = await fetch(url, {
          method: 'POST',
          credentials: 'include',
          headers: {
            channelId: '1',
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'User-Agent': 'neoload',
            TMXProfileID: getTMXSessionId(),
            Authorization: jwt,
          },
          body: JSON.stringify({
            nationalAgreementCodeRequest: {
              abbreviatedName: agreementCode,
              EIN: einCodeUserInput,
            },
          }),
        });

        if (response.status === 204) {
          newrelic.addPageAction(
            isEnroll
              ? PRICING_BOOK_NATIONAL_ENROLL
              : PRICING_BOOK_NATIONAL_UNENROLL,
            {
              result: 'success',
            },
          );

          return {
            data: 'Success',
            status: response.status,
            error: null,
            hasError: false,
          };
        }

        if (response.status === 401 || response.status === 431) {
          gotoLogonModule();
        }

        newrelic.addPageAction(
          isEnroll
            ? PRICING_BOOK_NATIONAL_ENROLL
            : PRICING_BOOK_NATIONAL_UNENROLL,
          {
            result: 'error',
            errorMessage:
              response.status === 401 || response.status === 431
                ? 'Authentication Error'
                : 'Agreement Code Could Not Be Found',
          },
        );

        return {
          data: {},
          status: response.status,
          error: 'Agreement Code Could Not Be Found',
          hasError: true,
        };
      } catch (error) {
        const errorMessage = errorMessageHelper(error);

        newrelic.addPageAction(
          isEnroll
            ? PRICING_BOOK_NATIONAL_ENROLL
            : PRICING_BOOK_NATIONAL_UNENROLL,
          {
            result: 'error',
            errorMessage,
          },
        );

        return {
          data: {},
          error: errorMessage,
          hasError: true,
        };
      }
    },
    [],
  );

  return (
    <NationalAccountContext.Provider
      value={{
        validResponseState,
        setValidResponseState,
        errorState,
        setErrorState,
        setShowModal,
        showModal,
        isLoading,
        setIsLoading,
        isMobile,
        setIsMobile,
        currentAgreementCode,
        setCurrentAgreementCode,
        fetchValidateAgreementCode,
        enrollAgreementCode,
      }}
    >
      {children}
    </NationalAccountContext.Provider>
  );
};

export { NationalAccountProvider, NationalAccountContext };
